import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { LeaseRouResult, LeaseRouSnapshot } from "@ranfac/shared";
import { downloadLeaseRouJe, fetchLeaseRou } from "../api.js";
import { WorkpaperActions } from "../components/WorkpaperActions.js";

export function LeaseRou() {
  const { period = "2026-01" } = useParams();
  const periodEnd = periodEndOf(period);
  const [data, setData] = useState<{
    snapshot: LeaseRouSnapshot;
    result: LeaseRouResult;
    source: "upload" | "fixture";
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchLeaseRou(periodEnd)
      .then(setData)
      .catch((e) => setErr(String(e)));
  }, [periodEnd]);

  if (err) return <div className="banner warn">Could not load: {err}</div>;
  if (!data) return <div className="card">Loading…</div>;

  const { result, source } = data;
  const balanced = Math.abs(result.assetLiabilityGap) < 1;

  async function onJe() {
    setBusy(true);
    try {
      const { filename, csv } = await downloadLeaseRouJe(periodEnd);
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      setToast(`Downloaded ${filename}.`);
    } catch (e) {
      setToast(`JE failed: ${e}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="card">
        <Link to={`/close/${period}`}>&larr; back to close dashboard</Link>
        <h1>Lease / ROU (Operating Lease, ASC 842)</h1>
        <div className="subtle">
          Period ending {result.periodEnd} ·{" "}
          {source === "upload" ? (
            <span className="badge" style={{ background: "#dcfce7", color: "#166534" }}>
              uploaded file
            </span>
          ) : (
            <span className="badge warn">
              sample data — upload the lease amortization schedule to use live numbers
            </span>
          )}
        </div>
        <div className="meta" style={{ marginTop: 16 }}>
          <div className="item">
            <div className="label">Source</div>
            <div>External lease amortization schedule (monthly row per period)</div>
          </div>
          <div className="item">
            <div className="label">Calculation</div>
            <div>
              Monthly amortization of {fmt(Math.abs(result.monthlyAmortization))} to{" "}
              {result.accounts.accumAmort} against {result.accounts.leaseExpense}.
            </div>
          </div>
          <div className="item">
            <div className="label">Phase</div>
            <div>2 · MVP</div>
          </div>
        </div>
      </div>

      <div className="kpi-grid">
        <Kpi label="Net ROU asset" value={fmt(result.netRouAsset)} />
        <Kpi label="Net lease liability" value={fmt(result.netLeaseLiability)} />
        <Kpi label="Monthly lease expense" value={fmt(result.monthlyLeaseExpense)} />
        <Kpi
          label="Asset + liability"
          value={fmt(result.assetLiabilityGap)}
          variant={balanced ? "ok" : "warn"}
        />
      </div>

      <div className="toolbar">
        <button onClick={onJe} disabled={busy}>
          Generate JE (CSV)
        </button>
      </div>

      <WorkpaperActions reconId="lease-rou" period={period} />

      <div className="card">
        <h2>Monthly rollforward</h2>
        <table>
          <thead>
            <tr>
              <th>GL</th>
              <th>Account</th>
              <th className="num">Beginning</th>
              <th className="num">Change</th>
              <th className="num">Ending</th>
            </tr>
          </thead>
          <tbody>
            {result.rollforward.map((r) => (
              <tr key={r.account}>
                <td className="monoish">{r.account}</td>
                <td>{r.label}</td>
                <td className="num">{fmt(r.beginning)}</td>
                <td className="num">{r.change === 0 ? "—" : fmt(r.change)}</td>
                <td className="num">{fmt(r.ending)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {toast && (
        <div className="toast" onClick={() => setToast(null)}>
          {toast}
        </div>
      )}
    </>
  );
}

function Kpi({
  label,
  value,
  variant,
}: {
  label: string;
  value: string | number;
  variant?: "ok" | "warn";
}) {
  return (
    <div className={`kpi ${variant ? "variance" : ""}`}>
      <div className="label">{label}</div>
      <div className={`value ${variant ?? ""}`}>{value}</div>
    </div>
  );
}

function fmt(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function periodEndOf(period: string): string {
  const [y, m] = period.split("-");
  const year = Number(y);
  const month = Number(m);
  const last = new Date(Date.UTC(year, month, 0));
  return last.toISOString().slice(0, 10);
}
